import { AnimatePresence, motion } from 'framer-motion';
import {
  Award,
  BarChart3,
  Briefcase,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  PieChart,
  Quote,
  Shield,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { Footer } from '@components/Footer';
import { Header } from '@components/Header';
import { Logo } from '@components/Logo';
import { SEOHead } from '@components/SEOHead';
import { ConditionalMotion } from '@components/ui/ConditionalMotion';

import { Button } from '@ui/Button';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

import {
  generateFAQStructuredData,
  generateFinancialServiceStructuredData,
  generateServiceStructuredData,
} from '@utils/structuredData';

// Hook to detect mobile screen size
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      const mobile = window.innerWidth < 768; // md breakpoint
      setIsMobile(mobile);
    };

    // Check immediately
    checkIsMobile();

    // Add event listener for resize
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

// Clean approach using ConditionalMotion component - no more complex helper functions needed!

// Taglines data - moved outside component to avoid re-renders
const TAGLINES = [
  'When your money matters, make every choice count',
  'From planning to prosperity, it all starts here',
  "From today's savings to tomorrow's dreams",
  'From hard work to wealth growth',
  'From every penny to every possibility',
  'One choice, endless growth',
  'Invest smart, live smart',
  'Success begins with the right choice',
];

type Testimonial = {
  id: string;
  quote: string;
  author: string;
  designation: string;
  location: string;
  color: string;
};

const TESTIMONIALS: Testimonial[] = [
  // {
  //   id: 'rahul-m',
  //   quote: `I was completely confused about mutual funds. ${CONTACT_INFO.contact_person.short_name} explained everything in simple terms and helped me start my first SIP. Now I'm investing ₹5,000 per month and actually understand where my money is going!`,
  //   author: 'Rahul M.',
  //   designation: 'Senior Software Engineer',
  //   location: 'Nashik',
  //   color: COLORS.sage,
  // },
  // {
  //   id: 'datt-d',
  //   quote: `I wanted to buy a house in 5 years but had no clue how to save for it. ${CONTACT_INFO.contact_person.short_name} created a clear plan with SIPs and debt funds. I'm already 10% there in just 6 months!`,
  //   author: 'Datt D.',
  //   designation: 'Freelancer',
  //   location: 'Pune',
  //   color: COLORS.gold,
  // },
];

// Animated Taglines Component
const AnimatedTaglines = () => {
  const [currentIndex, setCurrentIndex] = useState(() =>
    Math.floor(Math.random() * TAGLINES.length)
  );
  const [usedIndices, setUsedIndices] = useState<number[]>([]);

  useEffect(() => {
    const getNextRandomIndex = () => {
      let availableIndices = TAGLINES.map((_, index) => index).filter(
        index => !usedIndices.includes(index)
      );

      // If all taglines have been used, reset the used indices
      if (availableIndices.length === 0) {
        availableIndices = TAGLINES.map((_, index) => index);
        setUsedIndices([]);
      }

      const randomIndex =
        availableIndices[Math.floor(Math.random() * availableIndices.length)];
      return randomIndex;
    };

    const interval = setInterval(() => {
      const nextIndex = getNextRandomIndex();
      setCurrentIndex(nextIndex);
      setUsedIndices(prev => [...prev, nextIndex]);
    }, 4500); // Change every 4.5 seconds

    return () => clearInterval(interval);
  }, [usedIndices]);

  return (
    <div className="h-20 flex items-center justify-center relative">
      {/* Background glow effect */}
      <motion.div
        className="absolute inset-0 rounded-2xl opacity-20"
        style={{
          background: `radial-gradient(ellipse at center, ${COLORS.sage}15 0%, transparent 70%)`,
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.1, 0.3, 0.1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <AnimatePresence mode="wait">
        <motion.h1
          key={currentIndex}
          className="text-xl sm:text-2xl md:text-3xl font-light leading-relaxed text-center px-4 relative z-10"
          style={{
            color: 'rgba(248, 250, 252, 0.9)',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{
            duration: 0.6,
            ease: 'easeInOut',
          }}
        >
          {TAGLINES[currentIndex]}
        </motion.h1>
      </AnimatePresence>
    </div>
  );
};

export const HomePage = () => {
  // Mobile detection
  const isMobile = useIsMobile();

  // State for FAQ accordion
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // Toggle FAQ function
  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // FAQ data
  const faqs = [
    {
      question: 'I am completely new to investing. How do I start?',
      answer:
        "Just book a free 30-minute call with us. We'll understand your goals, explain everything in simple terms, and help you start with as little as ₹500 per month. We'll guide you through KYC, e-mandates, and all onboarding paperwork.",
      color: COLORS.accent,
    },
    {
      question: 'How much do you charge? Any hidden fees?',
      answer:
        "Zero hidden fees, ever. We earn a small commission from fund houses (already included in fund expense ratios), so our service is free for you. We'll show you exactly what you're paying in total costs before you invest even ₹1.",
      color: COLORS.sage,
    },
    {
      question: `Is my money safe? What if something happens to ${CONTACT_INFO.company.name}?`,
      answer: `Your money never comes to us - it goes directly to SEBI-regulated mutual fund companies like SBI, HDFC, ICICI, etc. Even if ${CONTACT_INFO.company.name} disappears tomorrow, your investments remain 100% safe in your name. We're just the bridge, not the vault.`,
      color: COLORS.gold,
    },
    {
      question: "What's the minimum amount I need to start investing?",
      answer:
        'You can start with as little as ₹500 per month through SIP (Systematic Investment Plan). For lumpsum investments, most funds require a minimum of ₹5,000. We recommend starting small and gradually increasing your investment as you get comfortable.',
      color: COLORS.accent,
    },
    {
      question: 'How do I know which mutual funds to choose?',
      answer:
        "That's exactly what we help you with! We analyze your goals, risk tolerance, and investment timeline to recommend the best funds. We consider factors like fund performance, expense ratios, fund manager track record, and how well they fit your specific needs.",
      color: COLORS.sage,
    },
    {
      question: 'Can I withdraw my money anytime? Are there any penalties?',
      answer:
        'Yes, mutual funds are highly liquid. You can redeem your investments anytime (except ELSS funds which have a 3-year lock-in). Most equity funds take 1-3 business days, and debt funds take 1-2 days for money to reach your bank account. No penalties from our side, ever.',
      color: COLORS.gold,
    },
    {
      question: 'What returns can I expect from mutual funds?',
      answer:
        "Historically, good equity funds have delivered 12-15% annual returns over 10+ years, while debt funds typically give 6-8%. However, returns vary and past performance doesn't guarantee future results. We focus on selecting consistent performers rather than chasing high returns.",
      color: COLORS.accent,
    },
    {
      question: 'Should I invest in direct plans or regular plans?',
      answer:
        'We recommend regular plans through us because you get ongoing guidance, portfolio reviews, and support - which often leads to better long-term results than going alone. The small difference in expense ratio (0.5-1%) is usually worth it for the professional guidance and peace of mind.',
      color: COLORS.sage,
    },
    {
      question: 'What documents do I need to start investing?',
      answer:
        "You'll need PAN card, Aadhaar card, bank account details, and a cancelled cheque. We'll help you complete KYC (Know Your Customer) process online, set up e-mandates for automatic investments, and handle all the paperwork. The entire process takes 15-20 minutes.",
      color: COLORS.gold,
    },
  ];

  // SEO: Add structured data for FAQ and Services
  useEffect(() => {
    // Generate dynamic structured data from contact constants
    const financialServiceData = generateFinancialServiceStructuredData();
    const faqStructuredData = generateFAQStructuredData();
    const serviceStructuredData = generateServiceStructuredData();

    // Add structured data to head
    const addStructuredData = (data: any, id: string) => {
      const existingScript = document.getElementById(id);
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    };

    addStructuredData(
      financialServiceData,
      'financial-service-structured-data'
    );
    addStructuredData(faqStructuredData, 'faq-structured-data');
    addStructuredData(serviceStructuredData, 'service-structured-data');

    // Cleanup function
    return () => {
      const financialScript = document.getElementById(
        'financial-service-structured-data'
      );
      const faqScript = document.getElementById('faq-structured-data');
      const serviceScript = document.getElementById('service-structured-data');
      if (financialScript) financialScript.remove();
      if (faqScript) faqScript.remove();
      if (serviceScript) serviceScript.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex flex-col">
      <SEOHead />
      <Header showNavigation={true} />

      {/* Elegant Hero Section */}
      <motion.section
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ scrollSnapAlign: 'start' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        role="banner"
        aria-label={`Hero section with ${CONTACT_INFO.company.name} introduction`}
      >
        {/* Sophisticated Background */}
        <div className="absolute inset-0">
          {/* Primary gradient */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 50%, ${COLORS.primary} 100%)`,
            }}
          />

          {/* Overlay patterns */}
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: `radial-gradient(circle at 25% 25%, ${COLORS.accent} 0%, transparent 50%), radial-gradient(circle at 75% 75%, ${COLORS.sage} 0%, transparent 50%)`,
              }}
            />
          </div>

          {/* Elegant geometric elements */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute top-20 right-20 w-96 h-96 rounded-full opacity-5"
              style={{
                background: `radial-gradient(circle, ${COLORS.gold} 0%, transparent 70%)`,
              }}
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
            <motion.div
              className="absolute bottom-20 left-20 w-64 h-64 rounded-full opacity-5"
              style={{
                background: `radial-gradient(circle, ${COLORS.sage} 0%, transparent 70%)`,
              }}
              animate={{
                scale: [1.1, 1, 1.1],
                rotate: [360, 180, 0],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto space-y-8 sm:space-y-12">
            {/* Certification Badge */}
            <motion.div
              className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-full backdrop-blur-sm border text-xs sm:text-sm font-medium"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: COLORS.pearl,
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="font-medium mr-2">AMFI certified</span>{' '}
              <motion.span
                className="w-2 h-2 rounded-full mr-2"
                style={{ backgroundColor: COLORS.sage }}
                animate={{ scale: [1, 1.25, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              />{' '}
              ARN: {CONTACT_INFO.amfi.arn}
            </motion.div>

            {/* Logo-Centric Design */}
            {/* Animated Taglines */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <AnimatedTaglines />
            </motion.div>
            <div className="space-y-8">
              {/* Main Logo - The Star */}
              <motion.div
                className="flex justify-center"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  duration: 1,
                  delay: 0.4,
                  type: 'spring',
                  stiffness: 100,
                }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="relative">
                  <div
                    className="absolute inset-0 rounded-3xl blur-2xl opacity-40"
                    style={{ backgroundColor: COLORS.gold }}
                  />
                  <Logo
                    variant="plus"
                    width={280}
                    height={154}
                    fill="white"
                    className="relative z-10 drop-shadow-2xl sm:w-[350px] sm:h-[193px] md:w-[400px] md:h-[220px]"
                    alt={`${CONTACT_INFO.company.name} - ${CONTACT_INFO.company.tagline}`}
                  />
                </div>
              </motion.div>
            </div>

            {/* Subtitle - More Subtle */}
            <motion.p
              className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-light px-4 sm:px-0"
              style={{ color: 'rgba(248, 250, 252, 0.7)' }}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              Your trusted partner for{' '}
              <span className="font-medium" style={{ color: COLORS.sage }}>
                smart mutual fund investing
              </span>{' '}
              and{' '}
              <span className="font-medium" style={{ color: COLORS.gold }}>
                personalized wealth building
              </span>{' '}
              strategies that actually work.
            </motion.p>

            {/* CTA Buttons - More Subtle */}
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 pt-4 sm:pt-6 px-4 sm:px-0"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              <Button
                href="/contact"
                variant="primary"
                shape="pill"
                size="md"
                className="w-full sm:w-auto"
              >
                Start investing today
              </Button>
            </motion.div>

            {/* Trust Indicators - Subtle */}
            <motion.div
              className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 pt-6 sm:pt-8 px-4 sm:px-0"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.4 }}
            >
              {[
                { icon: COLORS.sage, text: 'Trustworthy' },
                { icon: COLORS.accent, text: 'Transparent' },
                { icon: COLORS.gold, text: 'Experienced' },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: item.icon }}
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: index * 0.3,
                    }}
                  />
                  <span
                    className="text-xs sm:text-sm font-normal"
                    style={{ color: 'rgba(248, 250, 252, 0.6)' }}
                  >
                    {item.text}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* How it Works */}
      <ConditionalMotion
        as="section"
        animate={!isMobile}
        id="how"
        className="relative py-12 sm:py-16 lg:py-24 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${COLORS.pearl} 0%, ${COLORS.mist} 100%)`,
          scrollSnapAlign: 'start',
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
        role="main"
        aria-labelledby="how-it-works-heading"
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <ConditionalMotion
              as="h2"
              animate={!isMobile}
              id="how-it-works-heading"
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ color: COLORS.primary }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              How we work?
            </ConditionalMotion>
            <ConditionalMotion
              as="p"
              animate={!isMobile}
              className="text-xl max-w-3xl mx-auto leading-relaxed"
              style={{ color: COLORS.smoke }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              A simple, transparent process that takes you from confused
              investor to confident wealth builder in just 3 steps
            </ConditionalMotion>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: MessageSquare,
                title: 'Understand your dreams',
                description:
                  "Tell us about your goals - buying a home, child's education, retirement. We'll listen and understand what matters most to you.",
                color: COLORS.accent,
                delay: 0.6,
              },
              {
                icon: BarChart3,
                title: 'Create your plan',
                description:
                  "We'll recommend the best mutual funds for your goals. No jargon, no hidden fees - just clear explanations of why each fund makes sense.",
                color: COLORS.sage,
                delay: 0.8,
              },
              {
                icon: TrendingUp,
                title: 'Start & stay on track',
                description:
                  "We'll help you with KYC, e-mandates, paperwork and onboarding, then regularly check that you're on track to achieve your goals.",
                color: COLORS.gold,
                delay: 1.0,
              },
            ].map((step, index) => (
              <ConditionalMotion
                key={index}
                animate={!isMobile}
                className="group relative"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: step.delay }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <div className="relative bg-white rounded-3xl p-8 h-full shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-100">
                  {/* Number badge */}
                  <div
                    className="absolute -top-4 -left-4 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg"
                    style={{ backgroundColor: step.color }}
                  >
                    {index + 1}
                  </div>

                  <div className="flex flex-col items-center text-center pt-4">
                    <ConditionalMotion
                      animate={!isMobile}
                      className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300"
                      style={{ backgroundColor: `${step.color}15` }}
                      whileHover={{ rotate: 5 }}
                    >
                      <step.icon
                        className="w-10 h-10"
                        style={{ color: step.color }}
                      />
                    </ConditionalMotion>

                    <h3
                      className="font-bold text-2xl mb-4"
                      style={{ color: COLORS.primary }}
                    >
                      {step.title}
                    </h3>

                    <p
                      className="leading-relaxed"
                      style={{ color: COLORS.charcoal }}
                    >
                      {step.description}
                    </p>
                  </div>

                  {/* Hover effect */}
                  <motion.div
                    className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: `linear-gradient(135deg, ${step.color}05 0%, ${step.color}10 100%)`,
                      borderRadius: 'inherit',
                    }}
                  />
                </div>
              </ConditionalMotion>
            ))}
          </div>
        </div>
      </ConditionalMotion>

      {/* Elegant Why Choose Section */}
      <ConditionalMotion
        as="section"
        animate={!isMobile}
        id="about"
        className="relative py-12 md:py-24 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
          scrollSnapAlign: 'start',
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
        role="main"
        aria-labelledby="why-choose-us-heading"
      >
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute bottom-0 left-0 w-96 h-96 rounded-full opacity-5"
            style={{
              background: `radial-gradient(circle, ${COLORS.sage} 0%, transparent 70%)`,
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-20">
            <ConditionalMotion
              as="h2"
              animate={!isMobile}
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6"
              style={{ color: COLORS.pearl }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Why choose us?
            </ConditionalMotion>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="space-y-4 md:space-y-8">
              {[
                {
                  icon: Shield,
                  title: '100% transparent & certified',
                  description:
                    'AMFI-certified with no hidden agendas. We earn only when you invest, so our success depends on your success.',
                  color: COLORS.sage,
                  delay: 0.5,
                },
                {
                  icon: PieChart,
                  title: 'Unbiased fund selection, simplified',
                  description:
                    "Access to top-performing mutual funds, carefully selected and explained in simple terms you'll understand.",
                  color: COLORS.accent,
                  delay: 0.5,
                },
                {
                  icon: Users,
                  title: '24/7 support',
                  description:
                    "From opening your first account to achieving your biggest goals - we're with you every step of the way, for life.",
                  color: COLORS.gold,
                  delay: 0.5,
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 md:space-x-6 p-4 md:p-8 rounded-2xl md:rounded-3xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all duration-500"
                >
                  <div className="flex-shrink-0">
                    <motion.div
                      className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg"
                      style={{ backgroundColor: `${feature.color}25` }}
                      whileHover={
                        isMobile ? undefined : { scale: 1.1, rotate: 5 }
                      }
                      transition={isMobile ? undefined : { duration: 0.3 }}
                    >
                      <feature.icon
                        className="w-6 h-6 md:w-8 md:h-8"
                        style={{ color: feature.color }}
                      />
                    </motion.div>
                  </div>

                  <div className="flex-1">
                    <h3
                      className="font-bold text-lg md:text-2xl mb-2 md:mb-3"
                      style={{ color: COLORS.pearl }}
                    >
                      {feature.title}
                    </h3>
                    <p
                      className="leading-relaxed text-sm md:text-lg"
                      style={{ color: 'rgba(248, 250, 252, 0.85)' }}
                    >
                      {feature.description}
                    </p>
                  </div>

                  {/* Decorative element */}
                  <div
                    className="hidden lg:block flex-shrink-0 w-2 h-16 rounded-full opacity-30"
                    style={{ backgroundColor: feature.color }}
                  ></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ConditionalMotion>

      {/* Elegant Services Section */}
      <ConditionalMotion
        as="section"
        animate={!isMobile}
        id="services"
        className="relative py-24 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${COLORS.pearl} 0%, ${COLORS.mist} 100%)`,
          scrollSnapAlign: 'start',
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
        role="main"
        aria-labelledby="services-heading"
      >
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute top-0 left-0 w-96 h-96 rounded-full opacity-5"
            style={{
              background: `radial-gradient(circle, ${COLORS.gold} 0%, transparent 70%)`,
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <ConditionalMotion
              as="h2"
              animate={!isMobile}
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ color: COLORS.primary }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Our services
            </ConditionalMotion>
            <ConditionalMotion
              as="p"
              animate={!isMobile}
              className="text-xl max-w-3xl mx-auto leading-relaxed"
              style={{ color: COLORS.smoke }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              Everything you need to build wealth systematically
            </ConditionalMotion>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: Briefcase,
                title: 'Smart mutual fund selection',
                description:
                  "We pick the best funds from 1000+ options so you don't have to. Equity for growth, debt for stability, perfectly balanced for your goals.",
                color: COLORS.accent,
                delay: 0.6,
                features: [
                  'Fund analysis',
                  'Risk assessment',
                  'Performance tracking',
                ],
              },
              {
                icon: Target,
                title: 'Goal-based wealth building',
                description:
                  "Want to buy a house in 5 years? Retire comfortably? Fund your child's education? We'll create a specific plan with exact amounts and timelines.",
                color: COLORS.sage,
                delay: 0.8,
                features: [
                  'Custom planning',
                  'Timeline mapping',
                  'Progress tracking',
                ],
              },
              {
                icon: Award,
                title: 'Regular health check-ups',
                description:
                  'Just like your health, your investments need regular check-ups. We review and adjust your portfolio every quarter to keep you on track.',
                color: COLORS.gold,
                delay: 1.0,
                features: [
                  'Quarterly reviews',
                  'Rebalancing',
                  'Market updates',
                ],
              },
            ].map((service, index) => (
              <ConditionalMotion
                as="div"
                animate={!isMobile}
                key={index}
                className="group relative"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: service.delay }}
                viewport={{ once: true }}
                whileHover={{ y: -12 }}
              >
                <div className="relative bg-white rounded-3xl p-8 h-full shadow-xl hover:shadow-2xl transition-all duration-500 border border-slate-100 overflow-hidden group-hover:border-slate-200">
                  {/* Background decoration */}
                  <div
                    className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10 -mr-16 -mt-16 group-hover:opacity-12 transition-opacity duration-300"
                    style={{ backgroundColor: service.color }}
                  />

                  <div className="relative">
                    {/* Icon with enhanced styling */}
                    <div className="flex justify-center mb-6">
                      <ConditionalMotion
                        as="div"
                        animate={!isMobile}
                        className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300 relative"
                        style={{ backgroundColor: `${service.color}15` }}
                        whileHover={{ rotate: 5 }}
                      >
                        <service.icon
                          className="w-10 h-10"
                          style={{ color: service.color }}
                        />

                        {/* Glow effect on hover */}
                        <div
                          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                          style={{
                            backgroundColor: service.color,
                            filter: 'blur(4px)',
                          }}
                        />
                      </ConditionalMotion>
                    </div>

                    {/* Content */}
                    <div className="text-center">
                      <h3
                        className="font-bold text-2xl mb-4 group-hover:text-blue-700 transition-colors duration-300"
                        style={{ color: COLORS.primary }}
                      >
                        {service.title}
                      </h3>

                      <p className="leading-relaxed mb-6 text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                        {service.description}
                      </p>

                      {/* Enhanced feature list */}
                      <div className="flex justify-center mb-6">
                        <div className="space-y-3">
                          {service.features.map((feature, idx) => (
                            <ConditionalMotion
                              as="div"
                              animate={!isMobile}
                              key={idx}
                              className="flex items-center text-sm font-medium"
                              style={{ color: service.color }}
                              initial={{ opacity: 0, x: -10 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              transition={{
                                duration: 0.3,
                                delay: service.delay + idx * 0.1,
                              }}
                              viewport={{ once: true }}
                            >
                              <div
                                className="w-2 h-2 rounded-full mr-3 shadow-sm flex-shrink-0"
                                style={{ backgroundColor: service.color }}
                              />
                              {feature}
                            </ConditionalMotion>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: `linear-gradient(135deg, ${service.color}05 0%, ${service.color}10 100%)`,
                      borderRadius: 'inherit',
                    }}
                  />
                </div>
              </ConditionalMotion>
            ))}
          </div>

          {/* Learn More Section Button */}
          <ConditionalMotion
            as="div"
            animate={!isMobile}
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            viewport={{ once: true }}
          >
            <Button
              href="/services"
              variant="primary"
              size="lg"
              className="shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Learn more
            </Button>
            <p className="text-sm mt-3" style={{ color: COLORS.smoke }}>
              Discover our complete range of financial services and solutions.
            </p>
          </ConditionalMotion>
        </div>
      </ConditionalMotion>

      <ConditionalMotion
        as="section"
        className="relative py-24 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
          scrollSnapAlign: 'start',
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div
            className={`grid grid-cols-1 gap-16 ${TESTIMONIALS.length > 0 ? 'lg:grid-cols-2' : 'lg:grid-cols-1'}`}
          >
            {/* Testimonials */}
            {TESTIMONIALS.length > 0 && (
              <div>
                <h3
                  className="text-3xl font-bold mb-8"
                  style={{ color: COLORS.pearl }}
                >
                  What our clients say
                </h3>

                <div className="space-y-6">
                  {TESTIMONIALS.map(testimonial => (
                    <blockquote
                      key={testimonial.id}
                      className="relative p-8 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all duration-300"
                    >
                      <div
                        className="absolute -top-3 -left-3 w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: testimonial.color }}
                      >
                        <Quote className="w-6 h-6 text-white transform scale-x-[-1]" />
                      </div>
                      <p className="text-white/90 leading-relaxed mb-4">
                        "{testimonial.quote}"
                      </p>
                      <footer className="text-white/70 font-medium">
                        — {testimonial.author}, {testimonial.designation},{' '}
                        {testimonial.location}
                      </footer>
                    </blockquote>
                  ))}
                </div>
              </div>
            )}

            {/* Resources */}
            <div>
              <h3
                className="text-3xl font-bold mb-8"
                style={{ color: COLORS.pearl }}
              >
                Free financial resources
              </h3>

              <div className="space-y-4">
                <motion.a
                  href="/tools"
                  className="group w-full text-left p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all duration-300 block"
                  whileHover={isMobile ? undefined : { scale: 1.02 }}
                  whileTap={isMobile ? undefined : { scale: 0.98 }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-white mb-2 group-hover:text-white/90 text-lg">
                        Financial calculators & tools
                      </h4>
                      <p className="text-white/70 text-sm leading-relaxed">
                        SIP calculator, goal planner, tax calculator, and more
                        financial tools
                      </p>
                    </div>
                    <div className="text-white/50 group-hover:text-white/70 transition-colors">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </motion.a>

                <motion.a
                  href="/guides"
                  className="group w-full text-left p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all duration-300 block"
                  whileHover={isMobile ? undefined : { scale: 1.02 }}
                  whileTap={isMobile ? undefined : { scale: 0.98 }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-white mb-2 group-hover:text-white/90">
                        Investment guides
                      </h4>
                      <p className="text-white/70 text-sm leading-relaxed">
                        Comprehensive investment guides
                      </p>
                    </div>
                    <div className="text-white/50 group-hover:text-white/70 transition-colors">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </motion.a>
              </div>
            </div>
          </div>
        </div>
      </ConditionalMotion>

      <ConditionalMotion
        as="section"
        className="relative py-24 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${COLORS.pearl} 0%, ${COLORS.mist} 100%)`,
          scrollSnapAlign: 'start',
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-5"
            style={{
              background: `radial-gradient(circle, ${COLORS.accent} 0%, transparent 70%)`,
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <ConditionalMotion
              as="h2"
              animate={!isMobile}
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ color: COLORS.primary }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Frequently asked questions
            </ConditionalMotion>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-100"
              >
                <button
                  className="w-full text-left font-bold text-xl cursor-pointer flex items-center justify-between hover:text-blue-600 transition-colors duration-300"
                  style={{ color: COLORS.primary }}
                  onClick={() => toggleFAQ(index)}
                >
                  {faq.question}

                  <motion.span
                    className="ml-2 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
                    style={{
                      backgroundColor: `${faq.color}15`,
                      color: faq.color,
                    }}
                    whileHover={
                      isMobile
                        ? undefined
                        : {
                            scale: 1.1,
                            backgroundColor: faq.color,
                            color: 'white',
                          }
                    }
                    animate={{
                      rotate: openFAQ === index ? 180 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {openFAQ === index ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </motion.span>
                </button>

                <motion.div
                  initial={false}
                  animate={{
                    height: openFAQ === index ? 'auto' : 0,
                    opacity: openFAQ === index ? 1 : 0,
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  <p
                    className="mt-6 leading-relaxed text-lg"
                    style={{ color: COLORS.charcoal }}
                  >
                    {faq.answer}
                  </p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </ConditionalMotion>

      <Footer />
    </div>
  );
};

export default HomePage;
